import { Component, Host, h, Prop, JSX, State, Listen } from '@stencil/core'
import { ListingProduct, ProductDetailData } from '@rdss/client'
import { gotoPage, toMoney } from '@rdss/core'
import { DetailProduct } from '~/types'
import { trans, store } from '~/store'

@Component({
    tag: 'zr-product-variants',
    styleUrl: 'zr-product-variants.scss',
    shadow: true,
})

/**
 * ZooRoyal product variants component.
 */
export class ZrProductVariants {
    /**
     * Product Tile context.
     */
    @Prop() context = 'product-category-overlay'

    /**
     * Product.
     */
    @Prop() product: DetailProduct

    /**
     * Listing product representation.
     */
    @Prop() listingProduct: ListingProduct

    /**
     * Is sponsored.
     */
    @Prop() isSponsored = false

    /**
     * Variants.
     */
    @Prop() variants: {
        sku: string
        stock: number
    }[]

    /**
     * Path/URL of the cart page.
     */
    @Prop() cartUrl = '/checkout/cart'

    /**
     * The selected variant id.
     */
    @State() selectedVariantId: string

    /**
     * Show tooltip.
     */
    @State() showTooltip = false

    /**
     * Timeout for tooltip.
     */
    private showHideTimeout: ReturnType<typeof setTimeout> | null = null

    /**
     * Product variant comparator.
     */
    private variantComparator = (a: ProductDetailData, b: ProductDetailData) => {
        return a.unit.purchaseUnit - b.unit.purchaseUnit
    }

    /**
     * Product variant filter for sponsored products.
     */
    private variantFilter = (item: ProductDetailData) => {
        return !this.isSponsored || (this.product.ordernumber === item.id.toString())
    }

    /**
     * Checks if product is in cart.
     */
    getCartQuantity(sku: string): number {
        const item = store.state.cart?.items?.find(obj => obj.sku === sku)
        return item?.quantity || 0
    }

    /**
     * Get product stock for variants.
     */
    getVariantStock(sku: string): number {
        const item = this.variants?.find(obj => obj.sku === sku)
        return item?.stock || 0
    }

    /**
     * This helper function makes the browser jump to the shopping cart page.
     */
    private gotoCartPage = (): void => {
        if (location.pathname.includes(this.cartUrl)) return
        gotoPage(this.cartUrl)
    }

    /**
     * Activates the tooltip.
     */
    @Listen('handleQuantityChange')
    addCartItem({ detail } : CustomEvent<{ quantity: number, productId: number, context: string, increased: boolean }>) {
        this.selectedVariantId = detail.productId.toString()
        if(detail.increased) {
            this.showTooltip = true
            clearTimeout(this.showHideTimeout)
            this.showHideTimeout = setTimeout(() => {
                this.showTooltip = false
            }, 2000)
        }
    }

    /**
     * Renders the component.
     *
     * @returns {JSX.Element} The rendered component.
     */
    render(): JSX.Element {
        return (
            <Host>
                <div class="wrapper">
                    {(this.product?.media?.length > 0) && (
                        <vx-picture
                            src={this.product.media[0].file}
                            alt={this.product.title}
                            width={200}
                            fit="scale-down"
                        />
                    )}
                    <div class="manufacturer">{this.product?.manufacturer?.name}</div>
                    <h3 innerHTML={this.product?.title}/>
                    {this.listingProduct?.isBiocide && (
                        <div class="warning-info">
                            <zr-warning-notice
                                warning={trans(
                                    'frontend/detail/index.Warning',
                                    'Warnhinweis'
                                )}
                                notice={trans(
                                    'frontend/detail/index.Biocide',
                                    'Biozidprodukte vorsichtig verwenden. ' +
                                    'Vor Gebrauch stets Etikett und Produktinformationen lesen.'
                                )}
                            />
                        </div>
                    ) || this.listingProduct?.isVeterinary && (
                        <div class="warning-info">
                            <zr-warning-notice
                                warning={trans(
                                    'frontend/detail/index.VetNotice',
                                    'Beachte Folgendes bei der Fütterung von VET-Diätfutter:'
                                )}
                                notice={trans(
                                    'frontend/detail/index.VetFeedingInfo',
                                    'Füttere ein veterinärmedizinisches Diätfutter nur in Rücksprache mit deinem Tierarzt|' +
                                    'Lass dein Haustier während der Fütterung eines veterinärmedizinischen Produkts regelmäßig' +
                                    '(alle 6 Monate) zur Kontrolle von deinem Tierarzt untersuchen|Wende dich umgehend an ' +
                                    'deinen Tierarzt, sobald sich der Gesundheitszustand deines Haustieres verschlechtern sollte'
                                ).split('|')}
                            />
                            <zr-warning-notice
                                notice={trans(
                                    'frontend/detail/index.VetDisclaimer',
                                    'Sobald Du ein Diätfutter ausgewählt und in den Warenkorb gelegt hast, ' +
                                    'bestätigst Du automatisch, die oben genannten Hinweise gelesen und verstanden zu haben.'
                                )}
                            />
                        </div>
                    )}
                    {this.product?.details?.sort(this.variantComparator).filter(this.variantFilter).map((item) => {
                        const shortVariantName = item.name.replace(this.product.title, '').trim() || item.name
                        return (
                            <div class="variant-item">
                                <h4 class={{
                                    'variant-not-available': !item.isAvailable,
                                    'variant-main': item.id.toString() === this.product.ordernumber,
                                }} innerHTML={shortVariantName}/>
                                <div>
                                    <zr-price
                                        class={{ 'variant-not-available': !item.isAvailable }}
                                        pseudoPercent={item.price?.pseudoPercent}
                                        recommendedRetailPrice={item.price?.grossPseudo}
                                        gross={item.price?.gross}
                                        locale={this.listingProduct?.locale}
                                        currency={item.price?.currency}
                                        weightUnit={item.unit?.name}
                                        totalPriceLabel="Gesamtpreis"
                                        isPriceHighlight={false}
                                        referenceUnit={item.unit?.referenceUnit?.toString()}
                                        referencePriceUnit={item.price?.pricePerUnit}
                                    />
                                    <div class="variant-add">{item.isAvailable ? (
                                        <div>
                                            {(this.showTooltip && this.selectedVariantId === item.id.toString()) && (
                                                <zr-tooltip text={trans(
                                                    'frontend/listing/addtocart.addToCartTooltip',
                                                    'Hinzugefügt'
                                                )}/>
                                            )}
                                            <zr-add-to-cart-count
                                                cartQuantity={this.getCartQuantity(item.id.toString())}
                                                maxQuantity={this.getVariantStock(item.id.toString())}
                                                productId={item.id.toString()}
                                                context={this.context}
                                                ariaLabelAdd={trans(
                                                    'frontend/listing/addtocart.addXYProductToCart',
                                                    '%s zum Warenkorb hinzufügen',
                                                    [`${shortVariantName} ${toMoney(item.price?.gross, item.price?.currency, this.listingProduct?.locale)}`]
                                                )}
                                                ariaLabelRemove={trans(
                                                    'frontend/listing/addtocart.removeXYProductFromCart',
                                                    '%s aus dem Warenkorb entfernen',
                                                    [item.name]
                                                )}
                                            />
                                        </div>
                                    ) : (
                                        <button class="variant-add-disabled" disabled>
                                            <vx-icon icon="add_shopping_cart" invert={true}></vx-icon>
                                        </button>
                                    )}</div>
                                </div>
                            </div>
                        )
                    })
                    }
                    <div class="variant-to-cart__wrapper">
                        <vx-button
                            onClick={this.gotoCartPage}
                            fullwidth={true}
                            label={trans(
                                'frontend/index/cart.NoteToCart__BR2017',
                                'Zum Warenkorb'
                            )}
                            raised={true}>
                        </vx-button>
                    </div>
                </div>
            </Host>
        )
    }
}
